import { Session } from '~/models/auth';
import Profile, {
    Address,
    DriverLicense,
    HealthCard,
    LicensePlate,
    NocResponseType,
    PersonalDetails,
    PhotoCard,
    ProfileAccount,
    OriginalCard,
    Product,
    ProductValidationError,
    LicencePlateInquiryResponse,
    LicencePlateInquiryRequest,
} from '~/models/profile';
import { api, ApiResponse } from '~/services/api.service';
import { getCachedSession } from '~/utils/session-cache';
import { DriversLicenceType, PhotoCardType } from '~/components/products/types';

export type ProductKey = 'DL' | 'OPC';

function getUrlByProductKey(productKey: ProductKey): string {
    return productKey === 'DL'
        ? String(process.env.CXP_API_PRODUCTS_DRIVER_LICENCE_DETAILS ?? '')
        : String(process.env.CXP_API_PRODUCTS_PHOTO_CARD_DETAILS ?? '');
}

async function checkSession() {
    const session = (await getCachedSession()) as Session;
    if (!session) {
        throw new Error('Invalid Session');
    }
}

async function getProfile(): Promise<ApiResponse<Profile>> {
    checkSession();
    return api.get('CXP', `${process.env.CXP_API_PROFILE_ENDPOINT}`);
}

async function putProfileAccount(publicSecureID: string, emailID: string) {
    if (!publicSecureID || !emailID) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<ProfileAccount>('CXP', `${process.env.CXP_API_PROFILE_ACCOUNT_ENDPOINT}`, {
        publicSecureUniqueIdentifier: publicSecureID,
        publicSecureEmail: emailID,
    });
}

async function putCustomerDetails(detailsPayload?: PersonalDetails) {
    if (!detailsPayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<PersonalDetails>(
        'CXP',
        `${process.env.CXP_API_PROFILE_CUSTOMER_DETAILS}`,
        detailsPayload
    );
}

async function putAddress(addressPayload?: Address) {
    if (!addressPayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<Address>(
        'CXP',
        `${process.env.CXP_API_PROFILE_CUSTOMER_ADDRESS}`,
        addressPayload
    );
}

async function aiCheckDriversLicense(formData: FormData) {
    checkSession();
    return api.postForm(
        'FORM_RECOGNIZER',
        `${process.env.CXP_API_PROFILE_AIPHOTO_DRIVER_LICENSE}`,
        formData
    );
}

async function getOriginalDriverLicense() {
    checkSession();
    return api.get<DriverLicense>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_DRIVER_LICENSE}?masked=false`
    );
}

async function putDriverLicense(licensePayload?: DriverLicense) {
    if (!licensePayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<DriverLicense>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_DRIVER_LICENSE}`,
        licensePayload
    );
}

async function getOriginalPhotoCard() {
    checkSession();
    return api.get<PhotoCard>('CXP', `${process.env.CXP_API_PRODUCTS_PHOTO_CARD}?masked=false`);
}

async function putPhotoCard(photoCardPayload?: PhotoCard) {
    if (!photoCardPayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<PhotoCard>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_PHOTO_CARD}`,
        photoCardPayload
    );
}

async function getOriginalHealthCard() {
    checkSession();
    return api.get<HealthCard>('CXP', `${process.env.CXP_API_PRODUCTS_HEALTH_CARD}?masked=false`);
}

async function putHealthCard(healthCardPayload?: HealthCard) {
    if (!healthCardPayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<HealthCard>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_HEALTH_CARD}`,
        healthCardPayload
    );
}

async function putLicensePlate(licensePlatePayload?: LicensePlate) {
    if (!licensePlatePayload) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.put<LicensePlate>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_LICENSE_PLATE}`,
        licensePlatePayload
    );
}

async function getOriginalLicensePlate() {
    checkSession();
    return api.get<LicensePlate[]>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_LICENSE_PLATE}?masked=false`
    );
}

async function deleteDriverLicense(productId?: number) {
    if (!productId) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.delete<any>('CXP', `${process.env.CXP_API_PRODUCT_DELETE}`, undefined, {
        productId,
    });
}

async function deletePhotoCard(productId?: number) {
    if (!productId) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.delete<any>('CXP', `${process.env.CXP_API_PRODUCT_DELETE}`, undefined, {
        productId,
    });
}

async function deleteHealthCard(productId?: number) {
    if (!productId) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.delete<any>('CXP', `${process.env.CXP_API_PRODUCT_DELETE}`, undefined, {
        productId,
    });
}

async function deleteLicensePlate(productId?: number) {
    if (!productId) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.delete<any>('CXP', `${process.env.CXP_API_PRODUCT_DELETE}`, undefined, {
        productId,
    });
}

async function putProductsOnNocComplete(profile: Profile): Promise<NocResponseType | null> {
    const promiseList: Promise<
        ApiResponse<DriverLicense | HealthCard | LicensePlate | PhotoCard>
    >[] = [];
    const keys = ['driverLicense', 'healthCard', 'licencePlates', 'photoCard'];
    const params = keys.filter(key => !!profile.products?.[key]);

    params.forEach(param => {
        switch (param) {
            case 'driverLicense':
                promiseList.push(putDriverLicense(profile.products?.driverLicense));
                break;
            case 'healthCard':
                promiseList.push(putHealthCard(profile.products?.healthCard));
                break;
            case 'licencePlates':
                if (profile.products?.licencePlates?.length) {
                    promiseList.push(putLicensePlate(profile.products?.licencePlates[0]));
                }
                break;
            case 'photoCard':
                promiseList.push(putPhotoCard(profile.products?.photoCard));
                break;
            default:
                break;
        }
    });

    if (promiseList.length === 0) {
        return null;
    }

    let result: NocResponseType = {};
    const responses = await Promise.all(promiseList);
    responses.forEach((res, idx) => {
        result = {
            ...result,
            [params[idx]]: res,
        };
    });
    return result;
}

export async function getAllOriginalNumbers(
    driverLicense: boolean,
    healthCard: boolean,
    licencePlates: boolean,
    photoCard: boolean
): Promise<OriginalCard | undefined> {
    type ResultType = ApiResponse<DriverLicense | HealthCard | LicensePlate[] | PhotoCard>;
    let result: OriginalCard | undefined;
    const keys = ['driverLicense', 'healthCard', 'licencePlates', 'photoCard'];
    const params = [driverLicense, healthCard, licencePlates, photoCard]
        .map((param, index) => ({ key: keys[index], value: param }))
        .filter(param => param.value);
    if (params.length === 0) {
        return result;
    }

    const funcs: Promise<ResultType>[] = [];
    params.forEach(param => {
        switch (param.key) {
            case 'driverLicense':
                funcs.push(getOriginalDriverLicense());
                break;
            case 'healthCard':
                funcs.push(getOriginalHealthCard());
                break;
            case 'licencePlates':
                funcs.push(getOriginalLicensePlate());
                break;
            case 'photoCard':
                funcs.push(getOriginalPhotoCard());
                break;
            default:
                break;
        }
    });

    const responses = await Promise.all<ResultType>(funcs);
    responses.forEach((response, idx) => {
        const { key } = params[idx];
        let cardNumber = Array.isArray(response.data)
            ? response.data
                  ?.filter(product => product?.number)
                  .map(product => product.number as string)
            : response.data?.number;

        if (key === 'healthCard') {
            const card = response.data as HealthCard;
            cardNumber = cardNumber ? cardNumber + (card?.versionCode as string) : undefined;
        }
        if (!cardNumber) {
            return;
        }

        result = {
            ...(result ?? {}),
            [params[idx].key]: cardNumber,
        };
    });
    return result;
}

async function validateDriverLicense(driverLicence?: DriverLicense) {
    if (!driverLicence) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.post<DriverLicense | ProductValidationError>(
        'CXP',
        `${process.env.CXP_API_VALIDATE_DRIVER_LICENSE}`,
        driverLicence
    );
}

async function validateHealthCard(healthCard?: HealthCard) {
    if (!healthCard) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.post<Product | ProductValidationError>(
        'CXP',
        `${process.env.CXP_API_VALIDATE_HEALTH_CARD}`,
        healthCard
    );
}

async function validatePhotoCard(photoCard?: PhotoCard) {
    if (!photoCard) {
        throw new Error('Invalid arguments provided');
    }

    checkSession();
    return api.post<PhotoCard | ProductValidationError>(
        'CXP',
        `${process.env.CXP_API_VALIDATE_PHOTO_CARD}`,
        photoCard
    );
}

async function inquireLicencePlateWithMTO(request: LicencePlateInquiryRequest) {
    if (!request) {
        throw new Error('Inavlid arguments proviced');
    }

    return api.post<LicencePlateInquiryResponse>(
        'PRODUCT_RENEWAL',
        `${process.env.CXP_API_AVPV_INQUIRE}`,
        request
    );
}

async function getUnmaskedDriverLicenceNumber(
    productKey: ProductKey
): Promise<ApiResponse<{ driversLicenceNumber: string }>> {
    checkSession();
    return api.get<{ driversLicenceNumber: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=driversLicenceNumber&masked=false`
    );
}

async function getUnmaskedDriverLicenceTrilliumNumber(
    productKey: ProductKey
): Promise<ApiResponse<{ driversLicenceTrilliumNumber: string }>> {
    checkSession();
    return api.get<{ driversLicenceTrilliumNumber: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=driversLicenceTrilliumNumber&masked=false`
    );
}

async function getUnmaskedPhotoCardNumber(
    productKey: ProductKey
): Promise<ApiResponse<{ photoCardNumber: string }>> {
    checkSession();
    return api.get<{ photoCardNumber: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=photoCardNumber&masked=false`
    );
}

async function getUnmaskedPhotoCardDCN(
    productKey: ProductKey
): Promise<ApiResponse<{ photoCardDCN: string }>> {
    checkSession();
    return api.get<{ photoCardDCN: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=photoCardDCN&masked=false`
    );
}

async function getUnmaskedPostalCode(
    productKey: ProductKey
): Promise<ApiResponse<{ postalCode: string }>> {
    checkSession();
    return api.get<{ postalCode: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=postalCode&masked=false`
    );
}

async function getUnmaskedDateOfBirth(
    productKey: ProductKey
): Promise<ApiResponse<{ dateOfBirth: string }>> {
    checkSession();
    return api.get<{ dateOfBirth: string }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=dateOfBirth&masked=false`
    );
}

async function getUnmaskedAddressResidential(
    productKey: ProductKey
): Promise<ApiResponse<{ addressResidential: Address }>> {
    checkSession();
    return api.get<{ addressResidential: Address }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=addressResidential&masked=false`
    );
}

async function getUnmaskedAddressMailing(
    productKey: ProductKey
): Promise<ApiResponse<{ addressMailing: Address }>> {
    checkSession();
    return api.get<{ addressMailing: Address }>(
        'CXP',
        `${getUrlByProductKey(productKey)}?field=addressMailing&masked=false`
    );
}

async function getPhotoCardInfo(): Promise<ApiResponse<PhotoCardType>> {
    checkSession();
    return api.get<PhotoCardType>('CXP', `${process.env.CXP_API_PRODUCTS_PHOTO_CARD_DETAILS}`);
}

async function getDriversLicenceInfo(): Promise<ApiResponse<DriversLicenceType>> {
    checkSession();
    return api.get<DriversLicenceType>(
        'CXP',
        `${process.env.CXP_API_PRODUCTS_DRIVER_LICENCE_DETAILS}`
    );
}

const profileAPi = {
    getProfile,
    getOriginalDriverLicense,
    getOriginalHealthCard,
    getOriginalPhotoCard,
    getOriginalLicensePlate,
    getAllOriginalNumbers,
    getUnmaskedDriverLicenceNumber,
    getUnmaskedDriverLicenceTrilliumNumber,
    getUnmaskedPhotoCardNumber,
    getUnmaskedPhotoCardDCN,
    getUnmaskedPostalCode,
    getUnmaskedDateOfBirth,
    getUnmaskedAddressResidential,
    getUnmaskedAddressMailing,
    getPhotoCardInfo,
    getDriversLicenceInfo,
    putProfileAccount,
    putCustomerDetails,
    putAddress,
    putDriverLicense,
    aiCheckDriversLicense,
    putPhotoCard,
    putHealthCard,
    putLicensePlate,
    putProductsOnNocComplete,
    deleteDriverLicense,
    deletePhotoCard,
    deleteLicensePlate,
    deleteHealthCard,
    validateDriverLicense,
    validateHealthCard,
    validatePhotoCard,
    inquireLicencePlateWithMTO,
};

export default profileAPi;
